import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaInstagram, FaLinkedin, FaFacebook } from 'react-icons/fa';
import heroVideo from '../assets/videos/rent-hero.mp4';

import { propertiesForRent } from '../data/properties';
import { familyCommunities } from '../data/communities';

const longTermRentals = propertiesForRent;

const leasingFaqs = [
  {
    q: 'What documents do I need to rent a property?',
    a: 'You\'ll need a valid passport, visa, Emirates ID, and proof of income. Some landlords may require additional documents.'
  },
  {
    q: 'How long are typical rental contracts?',
    a: 'Most rental contracts are for 12 months, but we can arrange flexible terms from 6 months to 2 years.'
  },
  {
    q: 'What\'s included in the rent?',
    a: 'This varies by property. Some include utilities and maintenance, while others are rent-only. We\'ll clarify all details.'
  },
  {
    q: 'Can I negotiate the rent?',
    a: 'Yes, we can help negotiate terms and rent amounts based on market conditions and your requirements.'
  },
  {
    q: 'What are the payment terms?',
    a: 'Most landlords prefer 1-4 cheques per year. We can arrange payment plans that work for your budget.'
  }
];

function RentPage() {
  const [openFaq, setOpenFaq] = useState(null);


  return (
    <div className="flex flex-col min-h-screen font-sans bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover z-0"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black/60 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16">
            <h1 className="text-4xl md:text-6xl font-light text-white mb-6 drop-shadow-lg tracking-tight">
              Flexible Rental Options Across Dubai
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow max-w-3xl mx-auto leading-relaxed font-sans">
              Discover flexible, affordable, and premium rental properties tailored to all lifestyles and budgets. Whether you're relocating, upgrading, or renting short-term, we have the perfect home for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#rentals" className="px-8 py-4 rounded-lg bg-accent text-white font-medium text-lg hover:bg-accent/90 transition-colors duration-200 font-sans">
                View Rentals
              </a>
              <a href="#contact" className="px-8 py-4 rounded-lg bg-transparent text-white font-medium text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200 font-sans">
                Contact Agent
              </a>
            </div>
          </div>
        </section>



        {/* Long-Term Rentals */}
        <section id="rentals" className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
            Long-Term Rentals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {longTermRentals.map((property) => (
              <div key={property.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-64">
                  <img
                    src={property.img}
                    alt={property.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-accent text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {property.type}
                  </div>
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {property.price}
                  </div>
                  {property.furnished && (
                    <div className="absolute bottom-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Furnished
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{property.title}</h3>
                  <p className="text-gray-600 mb-4">{property.location}</p>
                  <div className="flex justify-between text-gray-500 text-sm mb-4">
                    <span>{property.beds} Beds</span>
                    <span>{property.baths} Baths</span>
                    <span>{property.sqft} sq.ft</span>
                  </div>
                  <Link to={`/property/${property.id}`} className="block w-full bg-accent text-white py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors duration-200 text-center">
                    Schedule Viewing
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Family-Friendly Communities */}
        <section className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
            Family-Friendly Communities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {familyCommunities.map((community, idx) => (
              <div key={community.name} className="relative rounded-2xl overflow-hidden group cursor-pointer">
                <img
                  src={community.img}
                  alt={community.name}
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/70 transition-colors duration-300" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="text-lg font-semibold text-white mb-1">{community.name}</h3>
                  <p className="text-white/80 text-sm">{community.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>



        {/* Leasing FAQs */}
        <section className="max-w-3xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
            Leasing FAQs
          </h2>
          <div className="space-y-4">
            {leasingFaqs.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl bg-white shadow-sm">
                <button
                  className="w-full flex justify-between items-center px-6 py-4 text-lg font-semibold text-gray-900 text-left focus:outline-none"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                >
                  <span>{faq.q}</span>
                  <svg className={openFaq === idx ? 'w-6 h-6 rotate-180 transition' : 'w-6 h-6 transition'} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4 text-gray-600 text-base animate-fadeIn">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 px-4">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
            style={{
              backgroundImage: `url('/images/communities/The Oasis.png')`,
            }}
          />
          <div className="absolute inset-0 bg-black/70 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6 tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Ready to Find Your Perfect Rental?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Our team is here to help you find the ideal rental property that fits your lifestyle and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#rentals" className="px-8 py-4 rounded-lg bg-accent text-white font-semibold text-lg hover:bg-accent/90 transition-colors duration-200">
                Browse Rentals
              </a>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+971505606877" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Mobile
                </a>
                <a href="tel:+97144323080" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Landline
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default RentPage; 